import React, { useMemo, useState } from "react";
import { Switch, Route, useRouteMatch } from "react-router-dom";
import { Card, Row, Button, Stack } from "react-bootstrap";
import { Helmet } from "react-helmet";
import {
  StyledCard,
  PersonName,
  PersonTitle,
  PersonDescription,
  StyledLink,
  PeopleCol,
} from "./styles";
import { StyledTitle } from "../../styles";
import Person from "./Person";

const PeopleCard = ({ person, section }) => {
  return (
    <PeopleCol>
      <StyledCard>
        {person.image && (
          <Card.Img
            variant="top"
            src={process.env.PUBLIC_URL + "/Image/people/" + person.image}
          />
        )}
        <Card.Body>
          <PersonName>{person.name}</PersonName>
          <PersonTitle>{person.title}</PersonTitle>
          <PersonDescription>{person.description}</PersonDescription>
        </Card.Body>
      </StyledCard>
    </PeopleCol>
  );
};

const Position = ({ people }) => {
  return Object.entries(people).map((pair, index) => (
    <React.Fragment key={pair[0]}>
      {/* <Container key={index}> */}
      <hr />
      <StyledTitle id={pair[0]}>{pair[0]}</StyledTitle>
      <Row xs={1} md={4}>
        {pair[1].map((person, index) => (
          <StyledLink
            key={index}
            href={
              process.env.PUBLIC_URL +
              "/people/" +
              person.name.trim().replaceAll(" ", "-") +
              "/"
            }
          >
            <PeopleCard person={person} section={pair[0]} />
          </StyledLink>
        ))}
      </Row>
      {/* </Container> */}
    </React.Fragment>
  ));
};

const People = ({ papers, people, peopleDict }) => {
  let { path } = useRouteMatch();

  const roles = Object.keys(people);

  // advisor to role to array of names
  const advisorToPeople = Object.fromEntries(
    people["Faculty"].map((person) => [
      person.name,
      Object.fromEntries(roles.map((role) => [role, []])),
    ])
  );
  // advisee name to list of advisor names
  const peopleToAdvisors = {};

  Object.entries(people).forEach(([role, persons]) => {
    persons.forEach((person) => {
      if (person.supervisors) {
        person.supervisors.forEach((supervisorName) => {
          const supervisor = advisorToPeople[supervisorName];
          // a student may have an advisor who is not in the lab,
          // in which case the advisor won't be in advisorToPeople
          if (supervisor) {
            supervisor[role].push(person.name);
            // also populate reverse dict of advisees -> advisors,
            // Exclude friends and collaborators from being included as advisees
            if (role !== "Friends & collaborators @ Khoury") {
              if (person.name in peopleToAdvisors) {
                peopleToAdvisors[person.name].push(supervisorName);
              } else {
                peopleToAdvisors[person.name] = [supervisorName];
              }
            }
          }
        });
      }
    });
  });

  return (
    <Switch>
      <Route exact path={`${path}/add`}>
        <AddEditPerson people={people} />
      </Route>
      <Route exact path={path}>
        <Position people={people} />
      </Route>
      <Route path={`${path}/:personId`}>
        <Person
          advisorToPeople={advisorToPeople}
          peopleToAdvisors={peopleToAdvisors}
          peopleDict={peopleDict}
          papers={papers}
        />
      </Route>
    </Switch>
  );
};

export default People;

const copyToClipboard = async (text) => {
  await navigator.clipboard.writeText(text);
};

const AddEditPerson = ({ people }) => {
  const [outputJson, setOutputJson] = useState(null);
  const [agree, setAgree] = useState(false);
  const [mode, setMode] = useState("add"); // 'add' | 'edit'
  const [selectedKey, setSelectedKey] = useState(""); // format: `${category}||${name}`
  const [prefill, setPrefill] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const categories = Object.keys(people || {});

  const options = useMemo(() => {
    const list = [];
    Object.entries(people || {}).forEach(([category, persons]) => {
      (persons || []).forEach((p) => {
        list.push({
          key: `${category}||${p.name}`,
          category,
          name: p.name,
          label: `${p.name} — ${category}`,
          person: p,
        });
      });
    });
    // sort by name asc
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [people]);

  function switchMode(next) {
    setMode(next);
    if (next === "add") {
      setPrefill(null);
      setSelectedKey("");
      setFormKey((k) => k + 1);
    }
  }

  function handleSelectExisting(e) {
    const key = e.target.value;
    setSelectedKey(key);
    const found = options.find((o) => o.key === key);
    if (!found) {
      setPrefill(null);
      return;
    }
    const { category, person } = found;
    // derive defaults
    const alias = Array.isArray(person.alias) ? person.alias.join(", ") : "";
    const supervisors = Array.isArray(person.supervisors)
      ? person.supervisors.join(", ")
      : "";
    const imageExt = (person.image || "").split(".").pop() || "jpg";
    const materials = person.materials || {};
    const pre = {
      name: person.name || "",
      title: person.title || "",
      category,
      alias,
      supervisors,
      description: person.description || "",
      imageUrl: "",
      imageExt,
      materials,
    };
    setPrefill(pre);
    // force remount so defaultValue takes effect for uncontrolled fields
    setFormKey((k) => k + 1);
  }

  function handleSubmit(e) {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);
    const raw = Object.fromEntries(formData.entries());

    const selectedCategory = raw.category || "Faculty";

    // for edit mode, capture previous identifiers
    let previousName = null;
    let previousCategory = null;
    if (mode === "edit" && selectedKey) {
      const [pcat, pname] = selectedKey.split("||");
      previousName = pname;
      previousCategory = pcat;
    }

    const aliasArray = (raw.alias || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const supervisorsArray = (raw.supervisors || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    // Compute image filename from name if not provided explicitly
    const baseName = (raw.name || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");
    const imageExt = (raw.imageExt || "jpg").replace(".", "");
    let imageFile = baseName ? `${baseName}-424x424.${imageExt}` : "";
    if (mode === "edit" && prefill && (!raw.imageUrl || raw.imageUrl.trim() === "")) {
      // Preserve existing image if user didn't supply a new URL
      const existing = options.find((o) => o.key === selectedKey)?.person;
      if (existing && existing.image) {
        imageFile = existing.image;
      }
    }

    const materialKeys = [
      "Homepage",
      "College Bio Page",
      "Roux Bio Page",
      "ORCID",
      "OSF",
      "Google Scholar",
      "DBLP",
      "GitHub",
      "Lab GitHub",
      "Observable",
      "Blog",
      "YouTube",
      "LinkedIn",
      "Mastodon",
      "Twitter",
      "Email",
      "CV",
    ];
    const materials = {};
    materialKeys.forEach((k) => {
      const v = raw[`materials_${k}`] || "";
      if (v.trim() !== "") {
        materials[k] = v.trim();
      }
    });

    const person = {
      name: raw.name || "",
      title: raw.title || "",
      description: raw.description || "",
      image: imageFile,
      materials,
    };
    if (aliasArray.length > 0) person.alias = aliasArray;
    if (supervisorsArray.length > 0) person.supervisors = supervisorsArray;

    const payload = {
      category: selectedCategory,
      ...person,
      ...(mode === "edit"
        ? {
            action: "update",
            previousName: previousName || person.name,
            previousCategory: previousCategory || selectedCategory,
          }
        : {}),
    };

    setOutputJson(payload);
  }

  const jsonString = outputJson ? JSON.stringify(outputJson, null, 2) : "";

  return (
    <div>
      <h3>Add or edit a person</h3>
      <Helmet>
        <title>Add or edit a person - Khoury Vis Lab</title>
        <meta name="description" content="Add or edit a person" />
      </Helmet>

      <p>Please enter ALL details for the person here.</p>

      <div style={{ marginBottom: 12 }}>
        <label>
          <input
            type="radio"
            name="mode"
            value="add"
            checked={mode === "add"}
            onChange={() => switchMode("add")}
          />{" "}
          Add
        </label>{" "}
        <label style={{ marginLeft: 16 }}>
          <input
            type="radio"
            name="mode"
            value="edit"
            checked={mode === "edit"}
            onChange={() => switchMode("edit")}
          />{" "}
          Edit
        </label>
      </div>

      {mode === "edit" && (
        <div style={{ marginBottom: 12 }}>
          <label>
            <strong>Select existing person</strong>
            <br />
            <select value={selectedKey} onChange={handleSelectExisting}>
              <option value="">-- choose --</option>
              {options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <form key={formKey} method="post" onSubmit={handleSubmit}>
        <hr />
        <h4>Required metadata</h4>
        <p>
          <label>
            <strong>Name</strong>
            <br />
            <textarea
              name="name"
              rows={1}
              cols={120}
              defaultValue={prefill?.name || ""}
            />
          </label>
        </p>
        <p>
          <label>
            <strong>Title</strong>
            <br />
            <textarea
              name="title"
              rows={1}
              cols={120}
              defaultValue={prefill?.title || ""}
            />
          </label>
        </p>
        <p>
          <label>
            <strong>Category</strong>
            <br />
            <select name="category" defaultValue={prefill?.category}>
              {categories.map((c) => (
                <option value={c} key={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </p>

        <h4>Optional metadata</h4>
        <p>
          <label>
            <strong>Alias</strong> — comma-separated
            <br />
            <textarea
              name="alias"
              rows={1}
              cols={120}
              defaultValue={prefill?.alias || ""}
            />
          </label>
        </p>
        <p>
          <label>
            <strong>Supervisors</strong> — comma-separated
            <br />
            <textarea
              name="supervisors"
              rows={1}
              cols={120}
              defaultValue={prefill?.supervisors || ""}
            />
          </label>
        </p>
        <p>
          <label>
            <strong>Description</strong>
            <br />
            <textarea
              name="description"
              rows={3}
              cols={120}
              defaultValue={prefill?.description || ""}
            />
          </label>
        </p>

        <h4>Image</h4>
        <p>
          <label>
            <strong>Image URL</strong> — public link to 424x424 headshot (GDrive/Imgur ok)
            <br />
            <textarea
              name="imageUrl"
              rows={1}
              cols={120}
              defaultValue={prefill?.imageUrl || ""}
            />
          </label>
        </p>
        <p>
          <label>
            <strong>Image extension</strong> — jpg/png (default jpg)
            <br />
            <textarea
              name="imageExt"
              rows={1}
              cols={40}
              defaultValue={prefill?.imageExt || ""}
            />
          </label>
        </p>

        <hr />
        <h4>Materials (optional)</h4>
        {[
          "Homepage",
          "College Bio Page",
          "Roux Bio Page",
          "ORCID",
          "OSF",
          "Google Scholar",
          "DBLP",
          "GitHub",
          "Lab GitHub",
          "Observable",
          "Blog",
          "YouTube",
          "LinkedIn",
          "Mastodon",
          "Twitter",
          "Email",
          "CV",
        ].map((k) => (
          <p key={k}>
            <label>
              <strong>{k}</strong>
              <br />
              <textarea
                name={`materials_${k}`}
                rows={1}
                cols={120}
                defaultValue={prefill?.materials?.[k] || ""}
              />
            </label>
          </p>
        ))}

        <hr />
        <h4>Submitting</h4>
        <label>
          <input
            type="checkbox"
            defaultChecked={false}
            checked={agree}
            onChange={() => setAgree(!agree)}
          />
          {' '}I have filled required fields and checked for accuracy.
        </label>
        <Button type="submit" className="mt-3 d-block" disabled={!agree}>
          Step 1: Generate JSON
        </Button>
      </form>

      {outputJson && (
        <div>
          <Stack direction="horizontal" gap={3} className="mt-2">
            <Button
              onClick={() => {
                copyToClipboard(jsonString);
              }}
            >
              Step 2: Copy to clipboard
            </Button>
            <Button
              className="mt-2"
              onClick={() => {
                const isEdit = mode === "edit";
                const baseUrl = isEdit
                  ? "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=update_person&projects=&title=[UPDATE PERSON] "
                  : "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=new_person&projects=&template=new-person-template.md&title=[NEW PERSON] ";
                const title = encodeURIComponent(outputJson.name || "");
                const url = baseUrl + title;
                window.open(url);
              }}
            >
              Step 3: Paste into new issue
            </Button>
          </Stack>
          <pre>{jsonString}</pre>
        </div>
      )}
    </div>
  );
};

